import UTIF from 'utif';

import { CustomNotification, dialogFunction } from 'common/components';
import {
  DEFAULT_PRODUCT_IMAGERY,
  MARKETING_IMAGE_KEYS,
  OTHER_IMAGE_KEYS,
  PLANOGRAM_IMAGE_KEYS,
} from 'common/components/product-add/components/constants';

import { getPreviewImageUrl } from 'utils/previewImage';

export const checkIsTiffFile = (file) => {
  if (file) {
    return (
      file.type === 'image/tiff' ||
      file.type === 'image/tif' ||
      file.name.toLowerCase().endsWith('.tiff') ||
      file.name.toLowerCase().endsWith('.tif')
    );
  }
  return false;
};

export const checkIsPngFile = (file) => {
  if (file) {
    return (
      file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')
    );
  }
  return false;
};

export const dialogDeleteProductImagery = (options = {}) => {
  const {
    imageId,
    deleteMutation,
    onSuccess,
    onError,
    content,
    okText = 'Delete',
    cancelText = 'Cancel',
    ...restOptions
  } = options;

  return dialogFunction({
    type: 'warn',
    content: content || `Are you sure you want to delete the image?`,
    okText,
    cancelText,
    okButtonProps: {
      danger: true,
      loading: deleteMutation?.isLoading,
    },
    onOk: async () => {
      try {
        const response = await deleteMutation.mutateAsync(imageId);

        if (response?.isSuccess) {
          CustomNotification.success(
            response?.message || 'Image deleted successfully'
          );
          if (onSuccess) onSuccess(response);
        } else {
          CustomNotification.error(
            response?.message || 'Failed to delete image'
          );
          if (onError) onError(response);
          return Promise.reject(); // Keep modal open
        }
      } catch (error) {
        CustomNotification.error(
          error?.message || 'An error occurred while deleting the image'
        );
        if (onError) onError(error);
        return Promise.reject(); // Keep modal open
      }
    },
    ...restOptions,
  });
};

const DEFAULT_THUMB_SIZE = 300;

export const mappingPreviewUrlForProductImagery = (
  initialProductImagery = {}
) => {
  const imageKeys = Object.keys(DEFAULT_PRODUCT_IMAGERY);

  return imageKeys.reduce((acc, key) => {
    const imageId = initialProductImagery[key];

    if (imageId) {
      acc[key] = {
        imageId,
        previewUrl: getPreviewImageUrl(imageId, DEFAULT_THUMB_SIZE),
      };
    } else {
      acc[key] = null;
    }

    return acc;
  }, {});
};

export const mergeProductImagery = (currentImagery, apiImagery) => {
  const apiImageryMapped = mappingPreviewUrlForProductImagery(apiImagery);
  const merged = {};

  // Iterate through all image keys
  Object.keys(DEFAULT_PRODUCT_IMAGERY).forEach((key) => {
    const currentValue = currentImagery[key];

    // Keep current value only if it exists AND isDraft is true
    // Otherwise, use API value
    const shouldKeepCurrent =
      currentValue !== null && currentValue?.status === 'draft';

    merged[key] = shouldKeepCurrent ? currentValue : apiImageryMapped[key];
  });

  return merged;
};

export const resetProductImageryStatus = (productImagery) => {
  const reset = {};

  Object.keys(productImagery).forEach((key) => {
    const value = productImagery[key];

    if (value !== null && typeof value === 'object') {
      // Create a new object without the status property
      const { status, ...rest } = value;
      reset[key] = rest;
    } else {
      // Keep null values as is
      reset[key] = value;
    }
  });

  return reset;
};

export const convertTiffToPreview = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        // Decode TIFF
        const arrayBuffer = e.target.result;
        const ifds = UTIF.decode(arrayBuffer);

        if (!ifds || ifds.length === 0) {
          reject(new Error('No images found in TIFF file'));
          return;
        }

        // Get first image (you can handle multi-page TIFFs by accessing other ifds)
        const firstImage = ifds[0];
        UTIF.decodeImage(arrayBuffer, firstImage);

        // Convert to RGBA
        const rgba = UTIF.toRGBA8(firstImage);

        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = firstImage.width;
        canvas.height = firstImage.height;
        const ctx = canvas.getContext('2d');

        // Create ImageData and put it on canvas
        const imageData = ctx.createImageData(
          firstImage.width,
          firstImage.height
        );
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);

        // Convert canvas to data URL (PNG format)
        const previewUrl = canvas.toDataURL('image/png');
        resolve(previewUrl);
      } catch (error) {
        console.error('Error converting TIFF with UTIF:', error);
        reject(error);
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
};

export const getErrorMessageForImageType = (key) => {
  if (PLANOGRAM_IMAGE_KEYS.includes(key)) {
    return 'The planogram image must be of type TIF or PNG, and have a clipping path.';
  }

  if (MARKETING_IMAGE_KEYS.includes(key)) {
    return 'Supported types: PNG, TIFF, TIF.';
  }

  if (OTHER_IMAGE_KEYS.includes(key)) {
    return 'Supported types: JPEG, PNG, JPG, TIFF, TIF.';
  }

  return 'Invalid image type.';
};

export const extractImageIds = (obj) => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    acc[key] = value?.imageId ?? null;
    return acc;
  }, {});
};
